import { useState } from 'react';
import { errorText } from '~/lib/apiError';
import { queryErrorMessage } from '~/modules/query';
import { orDash } from '~/lib/orDash';
import { toast } from 'react-toastify';
import { Eye, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';

import { ToggleField, TextField, Select, toOptions } from '~/components/form';
import {
  Button,
  ConfirmModal,
  Modal,
  ModalActions,
  PanelTable,
  FormActions,
} from '~/components/ui';
import { CREDENTIAL_TYPES } from '~/components/Constants/mlsAgreementsConstants';
import {
  useCreateAgreementCredentialMutation,
  useDeleteAgreementCredentialMutation,
  useListAgreementCredentialsQuery,
  useRestoreAgreementCredentialMutation,
  useRevealAgreementCredentialSecretMutation,
  useUpdateAgreementCredentialMutation,
} from '~/modules/query/mlsAgreements';

import { isStale } from '~/lib/apiError';
import type { CredentialRow } from '~/components/pages/mls-agreements/types';

const FORM_ID = 'mls-agreements-credential-form';

const emptyForm: CredentialRow = {
  type: CREDENTIAL_TYPES[0],
  endpoint: '',
  username: '',
  secret: '',
  isActive: true,
};

// Admin-only — the ★ GET/POST/PUT/DELETE credential routes. Never render this for a globalUser.
export const CredentialsPanel = ({
  mlsId,
  showDeleted = false,
}: {
  mlsId: string;
  showDeleted?: boolean;
}) => {
  const [form, setForm] = useState<CredentialRow | null>(null);
  const [secretModal, setSecretModal] = useState<{ row: CredentialRow; secret: unknown } | null>(
    null,
  );
  const [deleting, setDeleting] = useState<CredentialRow | null>(null);
  const [saving, setSaving] = useState(false);

  const { data, error, refetch } = useListAgreementCredentialsQuery({
    mlsId,
    deleted: showDeleted || undefined,
  });
  const rows = Array.isArray(data)
    ? (data as CredentialRow[])
    : (data?.rows as CredentialRow[] | undefined);
  const [createCredential] = useCreateAgreementCredentialMutation();
  const [updateCredential] = useUpdateAgreementCredentialMutation();
  const [deleteCredential] = useDeleteAgreementCredentialMutation();
  const [restoreCredential] = useRestoreAgreementCredentialMutation();
  const [revealSecret] = useRevealAgreementCredentialSecretMutation();

  const save = async () => {
    if (!form) return;
    setSaving(true);
    try {
      if (form.id) {
        const { id, updatedAt, ...rest } = form;
        await updateCredential({ id, data: { ...rest, updatedAt } }).unwrap();
      } else {
        await createCredential({ ...form, mlsId }).unwrap();
      }
      toast.success('Credential saved');
      setForm(null);
    } catch (err) {
      if (isStale(err)) {
        toast.error('This credential changed since you loaded it — reload and try again.');
      } else {
        toast.error(errorText(err, 'The credential could not be saved. Try again.'));
      }
    } finally {
      setSaving(false);
    }
  };

  const remove = (row: CredentialRow) => {
    deleteCredential(row.id as string)
      .unwrap()
      .then(() => toast.success('Credential deleted'))
      .catch((err) =>
        toast.error(errorText(err, 'The credential set could not be deleted. Try again.')),
      );
  };

  const restore = (row: CredentialRow) => {
    restoreCredential(row.id as string)
      .unwrap()
      .then(() => toast.success('Credential restored'))
      .catch((err) =>
        toast.error(errorText(err, 'The credential set could not be restored. Try again.')),
      );
  };

  const viewSecret = (row: CredentialRow) => {
    revealSecret(row.id as string)
      .unwrap()
      .then((res) => setSecretModal({ row, secret: res?.secret ?? res }))
      .catch((err) => toast.error(errorText(err, 'The secret could not be shown. Try again.')));
  };

  const edit = <K extends keyof CredentialRow>(key: K, value: CredentialRow[K]) =>
    setForm((f) => (f ? { ...f, [key]: value } : f));

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Button
          size="sm"
          startIcon={<Plus className="size-4" aria-hidden="true" />}
          onClick={() => setForm({ ...emptyForm })}
          testId="mls-agreements-credential-add"
        >
          Add credential
        </Button>
      </div>
      <PanelTable<CredentialRow>
        ariaLabel="Credential sets"
        testId="mls-agreements-credentials"
        loading={!rows && !error}
        error={
          error
            ? { what: 'the credentials', message: queryErrorMessage(error), onRetry: refetch }
            : undefined
        }
        emptyMessage="No credential sets yet."
        emptyDescription="Add one to store this MLS's feed login."
        data={rows ?? []}
        getRowId={(r, i) => r.id ?? i}
        columns={[
          { header: 'Type', render: (r) => orDash(r.type) },
          { header: 'Endpoint', render: (r) => orDash(r.endpoint) },
          { header: 'Username', render: (r) => orDash(r.username) },
          {
            header: 'Active',
            render: (r) => (
              <>
                {r.isActive ? 'Yes' : 'No'}
                {r.deletedAt ? (
                  <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">(deleted)</span>
                ) : null}
              </>
            ),
          },
          {
            header: 'Actions',
            width: 'w-36',
            render: (r) => (
              <div className="flex gap-1">
                <Button
                  size="icon"
                  variant="ghost"
                  ariaLabel="View secret"
                  title="View secret"
                  onClick={() => viewSecret(r)}
                >
                  <Eye className="size-4" aria-hidden="true" />
                </Button>
                <Button
                  size="icon"
                  variant="ghost"
                  ariaLabel="Edit credential"
                  title="Edit"
                  disabled={!!r.deletedAt}
                  onClick={() => setForm(r)}
                >
                  <Pencil className="size-4" aria-hidden="true" />
                </Button>
                {r.deletedAt ? (
                  <Button
                    size="icon"
                    variant="ghost"
                    ariaLabel="Restore credential"
                    title="Restore"
                    onClick={() => restore(r)}
                  >
                    <RotateCcw className="size-4" aria-hidden="true" />
                  </Button>
                ) : (
                  <Button
                    size="icon"
                    variant="ghost"
                    ariaLabel="Delete credential"
                    title="Delete"
                    onClick={() => setDeleting(r)}
                  >
                    <Trash2 className="size-4" aria-hidden="true" />
                  </Button>
                )}
              </div>
            ),
          },
        ]}
      />

      <Modal
        isOpen={!!form}
        onClose={() => setForm(null)}
        busy={saving}
        title={form?.id ? 'Edit credential' : 'Add credential'}
        testId="mls-agreements-credential-modal"
        footer={
          <FormActions
            form={FORM_ID}
            discardLabel="Cancel"
            onDiscard={() => setForm(null)}
            busy={saving}
            saveLabel={form?.id ? 'Save changes' : 'Add credential'}
            busyLabel="Saving…"
            saveTestId="mls-agreements-credential-save"
          />
        }
      >
        {form && (
          <form
            id={FORM_ID}
            className="grid grid-cols-1 gap-4 sm:grid-cols-2"
            onSubmit={(e) => {
              e.preventDefault();
              save();
            }}
          >
            <Select
              id="mls-agreements-credential-type"
              label="Type"
              value={String(form.type ?? '')}
              options={toOptions(CREDENTIAL_TYPES)}
              onChange={(v) => edit('type', v)}
            />
            <TextField
              id="mls-agreements-credential-endpoint"
              testId="mls-agreements-credential-endpoint"
              label="Endpoint"
              value={form.endpoint || ''}
              onChange={(v) => edit('endpoint', v)}
            />
            <TextField
              id="mls-agreements-credential-username"
              testId="mls-agreements-credential-username"
              label="Username"
              value={form.username || ''}
              onChange={(v) => edit('username', v)}
              autoComplete="off"
            />
            <TextField
              id="mls-agreements-credential-secret"
              testId="mls-agreements-credential-secret"
              label="Secret"
              type="password"
              value={form.secret || ''}
              onChange={(v) => edit('secret', v)}
              autoComplete="new-password"
              helperText={form.id ? 'Leave blank to keep the current secret' : undefined}
            />
            <ToggleField
              id="mls-agreements-credential-active"
              testId="mls-agreements-credential-active"
              label="Active"
              text="Credential set is in use"
              checked={!!form.isActive}
              onChange={(v) => edit('isActive', v)}
            />
          </form>
        )}
      </Modal>

      <Modal
        isOpen={!!secretModal}
        onClose={() => setSecretModal(null)}
        size="small"
        title="Credential secret"
        testId="mls-agreements-credential-secret-modal"
        footer={<ModalActions onDismiss={() => setSecretModal(null)} />}
      >
        <p className="break-all font-mono text-sm text-gray-800 dark:text-white/90">
          {String(secretModal?.secret ?? '')}
        </p>
      </Modal>

      <ConfirmModal
        isOpen={!!deleting}
        title="Delete credential set"
        message={`Delete the ${deleting?.type ?? ''} credential set for ${deleting?.username || deleting?.endpoint || 'this MLS'}?`}
        confirmLabel="Delete"
        variant="danger"
        dangerNote="It leaves this list, and Toolbox has no way to bring it back."
        testId="mls-agreements-credential-delete"
        onClose={() => setDeleting(null)}
        onConfirm={() => {
          if (deleting) remove(deleting);
          setDeleting(null);
        }}
      />
    </div>
  );
};

export default CredentialsPanel;
